import type { CartItemReference } from '../models';
import { uuidGenerator } from '../utils/uuid-generator';
import { NewCartItemIdGenerator } from '../utils/new-cart-item-id-generator';
import { type ProductModel } from '@kidslabs/module-catalog';
import { type CartItemWithTypeInterface } from '../models/cart.model';
import type { ConfigurableCartItem } from '../types/front.graphql';

/**
 * ============================================================================
 * LOCAL-FIRST RECONCILIATION UTILITIES
 * ============================================================================
 * Smart reconciliation between Magento cart items and local references
 * No server dependency - all logic handled on client
 */

export interface ReconciliationResult {
    // Final reconciled references
    references: CartItemReference[];

    // Statistics for debugging and logging
    stats: ReconciliationStats;

    // Notifications for user feedback
    notifications: ReconciliationNotification[];

    // Gift items (price = 0) - separated for later logic assignment
    giftItems: CartItemWithTypeInterface[];
}

export interface ReconciliationStats {
    matched: number; // References matched with cart items
    created: number; // New references created
    updated: number; // References updated (quantity changes)
    deleted: number; // References deleted (stale)
    preserved: number; // Selected state preserved
    giftsDetected: number; // Gift items detected (price = 0)
}

export interface ReconciliationNotification {
    type: 'info' | 'warning' | 'error';
    message: string;
    itemsAffected: number;
}

/**
 * Extract product information from cart item
 * Handles ConfigurableCartItem specially:
 * - productId: from configured_variant.id (the selected variant)
 * - parentProduct: from product (the configurable parent)
 */
function extractProductInfo(cartItem: CartItemWithTypeInterface): {
    productId: number;
    parentProduct?: ProductModel;
} {
    // Check if this is a ConfigurableCartItem
    if (cartItem.__typename === 'ConfigurableCartItem') {
        const configurableItem = cartItem as ConfigurableCartItem;

        // For configurable items:
        // - productId is the variant ID from configured_variant
        // - parentProduct is the configurable product
        const variantId = configurableItem.configured_variant?.id;

        if (typeof variantId !== 'number') {
            console.error('[extractProductInfo] Invalid configured_variant ID for ConfigurableCartItem', {
                cartItemId: cartItem.id,
                variantId,
            });
            // Fallback to product.id if configured_variant is missing
            return {
                productId: typeof cartItem.product.id === 'number' ? cartItem.product.id : 0,
                parentProduct: undefined,
            };
        }

        return {
            productId: variantId,
            parentProduct: cartItem.product, // The configurable parent product
        };
    }

    // For other cart item types (SimpleCartItem, etc.)
    // productId is directly from product.id
    const productId = cartItem.product.id;

    if (typeof productId !== 'number') {
        console.error('[extractProductInfo] Invalid product ID', {
            cartItemId: cartItem.id,
            typename: cartItem.__typename,
        });
        return {
            productId: 0,
            parentProduct: undefined,
        };
    }

    return {
        productId,
        parentProduct: undefined,
    };
}

/**
 * Create a new reference from a cart item
 */
export function createReferenceFromCartItem(
    cartItem: CartItemWithTypeInterface,
    cartId: string,
    isSelected: boolean = false
): CartItemReference {
    const { productId, parentProduct } = extractProductInfo(cartItem);

    // Derive variant SKU for configurable products
    let sku: string | undefined;
    if (cartItem.__typename === 'ConfigurableCartItem') {
        const configurableItem = cartItem as ConfigurableCartItem;
        sku = configurableItem.configured_variant?.sku ?? cartItem.product?.sku ?? undefined;
    } else {
        sku = cartItem.product?.sku ?? undefined;
    }

    // Sync ID from outside to inside: cartItemId → cartItem.id
    const cartItemWithSyncedId = {
        ...cartItem,
        id: cartItem.id, // Ensure cartItem.id matches cartItemId
    };

    return {
        cartItemReferenceId: uuidGenerator.generate(),
        productId,
        cartId,
        cartItemId: cartItem.id, // Outside ID
        qty: cartItem.quantity,
        logics: [],
        isSelected,
        createdAt: new Date().toISOString(),
        cartItem: cartItemWithSyncedId, // Inside ID synced with outside
        parentProduct, // Store parent product for configurable items
        sku, // Variant SKU for configurable, product SKU for simple
    };
}

/**
 * CORE RECONCILIATION FUNCTION - REWRITTEN WITH PRIORITY LOGIC
 *
 * Reconciliation Logic:
 *
 * STEP 0: Filter stale references (cartId !== currentCartId)
 * - Remove references from previous/different carts
 * - Magento only returns active cart, so stale references are invalid
 * - Clean state ensures no data confusion
 *
 * STEP 1: Process server items → match with local references by cartItemId
 * - For each server item, find all local references with matching cartItemId
 * - Select ONE reference to set isSelected = true based on priority:
 *   Priority 1: No logics (no gifts/attachments)
 *   Priority 2: No parentCartItemReferenceId
 *   Priority 3: Others
 * - If already selected, leave it. Otherwise, set first match by priority to selected
 * - Update matched reference with server data (qty, cartItem)
 *
 * STEP 2: Process selected local references (not matched in Step 1)
 * - If cartItemId doesn't match any server item → leave as is (keep selected)
 * - If productId matches a server item → update cartItemId and cartId from server
 *
 * STEP 3: Process unselected local references (not matched in Step 1)
 * - Check if they can match any remaining server items by productId
 * - Apply same priority rules as Step 1
 *
 * STEP 4: Create new references for unprocessed server items
 * - Any server item not matched in previous steps → create new reference (selected = true)
 */
export function reconcileCartReferences(
    serverCartItems: CartItemWithTypeInterface[],
    localReferences: CartItemReference[],
    currentCartId: string
): ReconciliationResult {
    console.log('[Reconcile] Starting reconciliation with priority logic', {
        serverItems: serverCartItems.length,
        localRefs: localReferences.length,
        cartId: currentCartId,
    });

    const stats: ReconciliationStats = {
        matched: 0,
        created: 0,
        updated: 0,
        deleted: 0,
        preserved: 0,
        giftsDetected: 0,
    };

    const notifications: ReconciliationNotification[] = [];
    const finalReferences: CartItemReference[] = [];

    // ========== STEP 0: Filter stale references from different carts ==========
    console.log('[Reconcile] STEP 0: Filtering stale references from different carts');

    const validReferences: CartItemReference[] = [];
    const resetReferences: CartItemReference[] = [];

    localReferences.forEach(ref => {
        // Valid cases:
        // 1. cartId is undefined (new item, not yet synced)
        // 2. cartId matches currentCartId
        if (!ref.cartId || ref.cartId === currentCartId) {
            validReferences.push(ref);
        } else {
            // Stale: cartId exists but different from currentCartId
            // RESET instead of DELETE - preserve user's product selection and logics
            const resetRef: CartItemReference = {
                ...ref,
                cartId: undefined, // Reset cartId - not associated with any cart
                cartItemId: undefined, // Reset cartItemId - needs re-sync
                // cartItem preserved - contains valuable product data (SKU, name, price, variant info)
                // Keep: productId, qty, logics, isSelected, parentProduct, cartItem, etc.
                // User's selection and logic configuration are preserved
            };
            resetReferences.push(resetRef);
        }
    });

    if (resetReferences.length > 0) {
        const staleCarts = [
            ...new Set(localReferences.filter(r => r.cartId && r.cartId !== currentCartId).map(r => r.cartId)),
        ];

        console.warn('[Reconcile] Resetting stale references from previous carts', {
            count: resetReferences.length,
            staleCarts,
            currentCartId,
        });

        // Add notification for user awareness
        notifications.push({
            type: 'info',
            message: 'Cart session changed - items need re-selection',
            itemsAffected: resetReferences.length,
        });

        // Add reset references to validReferences for further processing
        // They will be treated as new items that need to be synced
        validReferences.push(...resetReferences);
    }

    console.log('[Reconcile] After filtering', {
        validRefs: validReferences.length,
        resetRefs: resetReferences.length,
        currentCartId,
    });

    // Track which references and server items have been processed
    const processedReferenceIds = new Set<string>();
    const processedServerItemIds = new Set<string>();

    // Build map of server cart items by cartItemId for quick lookup
    const serverCartItemsMap = new Map<string, CartItemWithTypeInterface>();
    const serverCartItemsByProductId = new Map<number, CartItemWithTypeInterface[]>();

    serverCartItems.forEach(item => {
        serverCartItemsMap.set(item.id, item);

        // Extract product info (handles ConfigurableCartItem specially)
        const { productId } = extractProductInfo(item);

        if (productId !== 0) {
            if (!serverCartItemsByProductId.has(productId)) {
                serverCartItemsByProductId.set(productId, []);
            }
            serverCartItemsByProductId.get(productId)!.push(item);
        }
    });

    /**
     * Helper: Calculate priority score for reference selection
     * Lower score = higher priority
     */
    const calculatePriority = (ref: CartItemReference): number => {
        // Priority 1: No logics (score = 1)
        if (!ref.logics || ref.logics.length === 0) {
            return 1;
        }

        // Priority 2: No parent reference (score = 2)
        if (!ref.parentCartItemReferenceId) {
            return 2;
        }

        // Priority 3: Others (score = 3)
        return 3;
    };

    /**
     * Helper: Sort references by priority
     */
    const sortByPriority = (refs: CartItemReference[]): CartItemReference[] => {
        return [...refs].sort((a, b) => {
            const priorityA = calculatePriority(a);
            const priorityB = calculatePriority(b);

            if (priorityA !== priorityB) {
                return priorityA - priorityB;
            }

            // If same priority, prefer already selected
            if (a.isSelected && !b.isSelected) return -1;
            if (!a.isSelected && b.isSelected) return 1;

            // If same priority and selection status, keep original order
            return 0;
        });
    };

    /**
     * Helper: Create unselected reference with new temporary ID
     * Used when a reference doesn't match with any server item
     */
    const createUnselectedReference = (ref: CartItemReference): CartItemReference => {
        // For unselected refs: generate new temporary ID using centralized method
        const tempCartItemId = NewCartItemIdGenerator.generateFromReference(
            ref.cartItem?.product?.id ?? '',
            ref.parentProduct
        );

        // Sync ID from outside to inside: cartItemId → cartItem.id
        const syncedUnselectedCartItem = ref.cartItem
            ? {
                  ...ref.cartItem,
                  id: tempCartItemId, // Inside ID synced with outside
              }
            : undefined;

        return {
            ...ref,
            cartId: undefined,
            cartItemId: tempCartItemId, // Outside ID
            isSelected: false,
            cartItem: syncedUnselectedCartItem, // Inside ID synced with outside
        };
    };

    // ========== STEP 1: Process server items and match with local references by cartItemId ==========
    console.log('[Reconcile] STEP 1: Processing server items with cartItemId matching');

    serverCartItems.forEach(serverItem => {
        // Use validReferences instead of localReferences
        const matchingRefs = validReferences.filter(ref => ref.cartItemId === serverItem.id);

        if (matchingRefs.length === 0) {
            // No matching reference found, will handle in Step 4
            return;
        }

        console.log('[Reconcile] Found matching references for server item', {
            serverItemId: serverItem.id,
            matchingRefsCount: matchingRefs.length,
        });

        // Sort by priority
        const sortedRefs = sortByPriority(matchingRefs);

        // Check if any is already selected
        const alreadySelected = sortedRefs.find(ref => ref.isSelected);

        if (alreadySelected) {
            // Keep the already selected reference
            console.log('[Reconcile] Keeping already selected reference', {
                refId: alreadySelected.cartItemReferenceId,
                priority: calculatePriority(alreadySelected),
            });

            finalReferences.push({
                ...alreadySelected,
                cartId: currentCartId,
                cartItemId: serverItem.id, // Outside ID
                qty: serverItem.quantity,
                isSelected: true,
                cartItem: { ...serverItem, id: serverItem.id }, // Inside ID synced with outside
            });

            stats.preserved++;
            stats.matched++;
            processedReferenceIds.add(alreadySelected.cartItemReferenceId);
            processedServerItemIds.add(serverItem.id);

            // Add other matching references as unselected
            sortedRefs.forEach(ref => {
                if (ref.cartItemReferenceId !== alreadySelected.cartItemReferenceId) {
                    finalReferences.push(createUnselectedReference(ref));
                    processedReferenceIds.add(ref.cartItemReferenceId);
                }
            });
        } else {
            // Select the first one by priority
            const selectedRef = sortedRefs[0];

            console.log('[Reconcile] Selecting reference by priority', {
                refId: selectedRef.cartItemReferenceId,
                priority: calculatePriority(selectedRef),
                hasLogics: (selectedRef.logics?.length ?? 0) > 0,
                hasParent: !!selectedRef.parentCartItemReferenceId,
            });

            finalReferences.push({
                ...selectedRef,
                cartId: currentCartId,
                cartItemId: serverItem.id, // Outside ID
                qty: serverItem.quantity,
                isSelected: true,
                cartItem: { ...serverItem, id: serverItem.id }, // Inside ID synced with outside
            });

            stats.matched++;
            processedReferenceIds.add(selectedRef.cartItemReferenceId);
            processedServerItemIds.add(serverItem.id);

            // Add other matching references as unselected
            sortedRefs.slice(1).forEach(ref => {
                finalReferences.push(createUnselectedReference(ref));
                processedReferenceIds.add(ref.cartItemReferenceId);
            });
        }
    });

    // ========== STEP 2: Process selected local references not matched in Step 1 ==========
    console.log('[Reconcile] STEP 2: Processing remaining selected references');

    // Use validReferences instead of localReferences
    const remainingSelectedRefs = validReferences.filter(
        ref => ref.isSelected && !processedReferenceIds.has(ref.cartItemReferenceId)
    );

    remainingSelectedRefs.forEach(ref => {
        // Check if cartItemId matches any server item
        const serverItem = ref.cartItemId ? serverCartItemsMap.get(ref.cartItemId) : null;

        if (serverItem) {
            // This case should have been handled in Step 1, but just in case
            console.warn('[Reconcile] Selected ref with matching cartItemId not processed in Step 1', {
                refId: ref.cartItemReferenceId,
                cartItemId: ref.cartItemId,
            });

            finalReferences.push({
                ...ref,
                cartId: currentCartId,
                cartItemId: serverItem.id, // Outside ID
                qty: serverItem.quantity,
                isSelected: true,
                cartItem: { ...serverItem, id: serverItem.id }, // Inside ID synced with outside
            });

            stats.matched++;
            processedReferenceIds.add(ref.cartItemReferenceId);
            processedServerItemIds.add(serverItem.id);
        } else {
            // cartItemId doesn't match any server item
            // Check if productId matches any unprocessed server item
            const matchingServerItems = serverCartItemsByProductId.get(ref.productId) || [];
            const unprocessedServerItem = matchingServerItems.find(item => !processedServerItemIds.has(item.id));

            if (unprocessedServerItem) {
                // Found matching server item by productId → update cartItemId and cartId
                console.log('[Reconcile] Updating selected ref with matching productId', {
                    refId: ref.cartItemReferenceId,
                    oldCartItemId: ref.cartItemId,
                    newCartItemId: unprocessedServerItem.id,
                    productId: ref.productId,
                });

                finalReferences.push({
                    ...ref,
                    cartId: currentCartId,
                    cartItemId: unprocessedServerItem.id, // Outside ID
                    qty: unprocessedServerItem.quantity,
                    isSelected: true,
                    cartItem: { ...unprocessedServerItem, id: unprocessedServerItem.id }, // Inside ID synced with outside
                });

                stats.updated++;
                processedReferenceIds.add(ref.cartItemReferenceId);
                processedServerItemIds.add(unprocessedServerItem.id);
            } else {
                // No matching server item found
                // CRITICAL: Check if cartId matches currentCartId
                // If cartId doesn't match or is stale → reset both cartId and cartItemId
                // This ensures selected items from old carts are treated as new items

                const hasValidCartId = ref.cartId === currentCartId;

                if (!hasValidCartId && ref.cartId) {
                    // cartId exists but doesn't match currentCartId → stale from old cart
                    // Reset cart-related IDs but KEEP isSelected state.
                    // The item will be re-synced to the new cart on the next sync cycle.
                    // Resetting isSelected to false would cause the item to be silently
                    // dropped from the Magento cart without user intent.
                    console.warn('[Reconcile] Selected ref has stale cartId, resetting IDs for re-sync', {
                        refId: ref.cartItemReferenceId,
                        oldCartId: ref.cartId,
                        currentCartId,
                        cartItemId: ref.cartItemId,
                        productId: ref.productId,
                    });

                    // Reset both cartId and cartItemId - will be set after sync
                    // Keep cartItem to preserve product data (SKU, name, price, variant info)
                    // Keep isSelected to preserve user's selection intent
                    finalReferences.push({
                        ...ref,
                        cartId: undefined, // Reset cartId
                        cartItemId: undefined, // Reset cartItemId
                        // isSelected preserved — user explicitly selected this item
                        // cartItem preserved — contains valuable product data
                    });

                    stats.preserved++;
                    processedReferenceIds.add(ref.cartItemReferenceId);
                } else {
                    // cartId is valid (matches currentCartId) or undefined
                    // Keep selected state - will be synced to server
                    console.log('[Reconcile] Keeping selected ref without server match (valid cartId)', {
                        refId: ref.cartItemReferenceId,
                        cartItemId: ref.cartItemId,
                        cartId: ref.cartId,
                        productId: ref.productId,
                    });

                    finalReferences.push({
                        ...ref,
                        isSelected: true,
                    });

                    stats.preserved++;
                    processedReferenceIds.add(ref.cartItemReferenceId);
                }
            }
        }
    });

    // ========== STEP 3: Process unselected local references not matched in Step 1 ==========
    console.log('[Reconcile] STEP 3: Processing remaining unselected references');

    // Use validReferences instead of localReferences
    const remainingUnselectedRefs = validReferences.filter(
        ref => !ref.isSelected && !processedReferenceIds.has(ref.cartItemReferenceId)
    );

    // Group unselected refs by productId
    const unselectedByProductId = new Map<number, CartItemReference[]>();
    remainingUnselectedRefs.forEach(ref => {
        if (!unselectedByProductId.has(ref.productId)) {
            unselectedByProductId.set(ref.productId, []);
        }
        unselectedByProductId.get(ref.productId)!.push(ref);
    });

    // Try to match with remaining server items by productId
    unselectedByProductId.forEach((refs, productId) => {
        const matchingServerItems = serverCartItemsByProductId.get(productId) || [];
        const unprocessedServerItems = matchingServerItems.filter(item => !processedServerItemIds.has(item.id));

        if (unprocessedServerItems.length > 0) {
            // Apply priority rules to select which reference to match
            const sortedRefs = sortByPriority(refs);

            unprocessedServerItems.forEach((serverItem, index) => {
                if (index < sortedRefs.length) {
                    const selectedRef = sortedRefs[index];

                    console.log('[Reconcile] Matching unselected ref with server item by productId', {
                        refId: selectedRef.cartItemReferenceId,
                        serverItemId: serverItem.id,
                        productId,
                        priority: calculatePriority(selectedRef),
                    });

                    finalReferences.push({
                        ...selectedRef,
                        cartId: currentCartId,
                        cartItemId: serverItem.id, // Outside ID
                        qty: serverItem.quantity,
                        isSelected: true,
                        cartItem: { ...serverItem, id: serverItem.id }, // Inside ID synced with outside
                    });

                    stats.matched++;
                    processedReferenceIds.add(selectedRef.cartItemReferenceId);
                    processedServerItemIds.add(serverItem.id);
                }
            });

            // Add remaining unmatched references as unselected
            sortedRefs.slice(unprocessedServerItems.length).forEach(ref => {
                finalReferences.push(createUnselectedReference(ref));
                processedReferenceIds.add(ref.cartItemReferenceId);
            });
        } else {
            // No matching server items, keep all as unselected
            refs.forEach(ref => {
                finalReferences.push(createUnselectedReference(ref));
                processedReferenceIds.add(ref.cartItemReferenceId);
            });
        }
    });

    // ========== STEP 4: Create new references for unprocessed server items ==========
    console.log('[Reconcile] STEP 4: Creating references for unmatched server items');

    serverCartItems.forEach(cartItem => {
        if (processedServerItemIds.has(cartItem.id)) {
            return;
        }

        // Extract product info (handles ConfigurableCartItem specially)
        const { productId, parentProduct } = extractProductInfo(cartItem);

        if (productId === 0) {
            console.error('[Reconcile] Invalid product ID', { cartItemId: cartItem.id });
            return;
        }

        // Detect gift items (price = 0) - don't create references for them
        const itemPrice = cartItem.prices?.price?.value ?? 0;
        if (itemPrice === 0) {
            console.log('[Reconcile] Detected gift item (price = 0), skipping reference creation', {
                cartItemId: cartItem.id,
                productId,
                typename: cartItem.__typename,
                price: itemPrice,
            });

            stats.giftsDetected++;
            processedServerItemIds.add(cartItem.id); // Mark as processed so we don't warn about it
            return;
        }

        console.log('[Reconcile] Creating new reference for unmatched server item', {
            cartItemId: cartItem.id,
            productId,
            typename: cartItem.__typename,
            hasParentProduct: !!parentProduct,
            price: itemPrice,
        });

        // Derive variant SKU for configurable products
        let sku: string | undefined;
        if (cartItem.__typename === 'ConfigurableCartItem') {
            const configurableItem = cartItem as ConfigurableCartItem;
            sku = configurableItem.configured_variant?.sku ?? cartItem.product?.sku ?? undefined;
        } else {
            sku = cartItem.product?.sku ?? undefined;
        }

        const newReference: CartItemReference = {
            cartItemReferenceId: uuidGenerator.generate(),
            productId,
            cartId: currentCartId,
            cartItemId: cartItem.id,
            qty: cartItem.quantity,
            logics: [],
            isSelected: true,
            createdAt: new Date().toISOString(),
            cartItem: cartItem,
            parentProduct, // Store parent product for configurable items
            sku, // Variant SKU for configurable, product SKU for simple
        };

        finalReferences.push(newReference);
        stats.created++;
    });

    // ========== STEP 5: Calculate statistics ==========
    // Note: stats.deleted already set in STEP 0, but we need to account for all deletions
    const totalDeleted = resetReferences.length + (validReferences.length - (finalReferences.length - stats.created));
    stats.deleted = totalDeleted > 0 ? totalDeleted : 0;

    console.log('[Reconcile] Reconciliation complete with priority logic', {
        stats,
        processedRefs: processedReferenceIds.size,
        processedServerItems: processedServerItemIds.size,
        finalRefsCount: finalReferences.length,
    });

    // Add notifications
    if (stats.created > 0) {
        notifications.push({
            type: 'info',
            message: `${stats.created} items added to cart`,
            itemsAffected: stats.created,
        });
    }

    if (stats.deleted > 0) {
        notifications.push({
            type: 'warning',
            message: `${stats.deleted} items removed from cart`,
            itemsAffected: stats.deleted,
        });
    }

    if (stats.updated > 0) {
        notifications.push({
            type: 'info',
            message: `${stats.updated} items updated`,
            itemsAffected: stats.updated,
        });
    }

    return {
        references: finalReferences,
        stats,
        notifications,
        giftItems: serverCartItems.filter(item => (item.prices?.price?.value ?? 0) === 0), // Return gift items separately
    };
}

/**
 * Validate cart ID consistency across references
 */
export function validateCartIdConsistency(
    references: CartItemReference[],
    expectedCartId: string
): {
    isValid: boolean;
    inconsistentRefs: CartItemReference[];
} {
    const inconsistentRefs = references.filter(ref => ref.cartId && ref.cartId !== expectedCartId);

    return {
        isValid: inconsistentRefs.length === 0,
        inconsistentRefs,
    };
}

/**
 * Auto-fix cart ID for references
 */
export function autoFixCartIds(references: CartItemReference[], correctCartId: string): CartItemReference[] {
    return references.map(ref => ({
        ...ref,
        cartId: correctCartId,
    }));
}

/**
 * Get references that should be synced to server
 * All references have cartItemId:
 * - If cartItemId starts with 'new-' → add new item to cart
 * - If cartItemId is real ID → update existing item
 */
export function getReferencesToSync(references: CartItemReference[]): CartItemReference[] {
    return references.filter(ref => {
        // Must be selected
        if (!ref.isSelected) {
            return false;
        }

        // Must have cartItemId (all references should have this)
        if (!ref.cartItemId) {
            return false;
        }

        // Both cases need to sync:
        // Case 1: Real cartItemId (not starting with 'new-') → for updates
        // Case 2: Temporary cartItemId (starting with 'new-') → for additions
        return true;
    });
}

/**
 * Check if reconciliation is needed
 */
export function shouldReconcile(
    serverCartItems: CartItemWithTypeInterface[],
    localReferences: CartItemReference[]
): boolean {
    // Always reconcile if counts don't match
    if (serverCartItems.length !== localReferences.length) {
        return true;
    }

    // Check if all server items have matching references
    const serverItemIds = new Set(serverCartItems.map(item => item.id));
    const localItemIds = new Set(localReferences.map(ref => ref.cartItemId).filter(Boolean));

    // Check if sets are equal
    if (serverItemIds.size !== localItemIds.size) {
        return true;
    }

    for (const id of serverItemIds) {
        if (!localItemIds.has(id)) {
            return true;
        }
    }

    return false;
}
